import type {
  ListingsOrderByDirection,
  ListingsOrderByProperty,
} from "@/lib/types";
import { buildLocationUrlSegments, formatLocation } from "@/lib/locations";
import type { BoundingBox, MapCoords, SearchHookLocation } from "@repo/maps";
import type { OrderByValue } from "./businesses-view-types";

// "relevance_desc" (raw opportunity score) is still accepted by the API but is
// no longer offered in the UI: scores are internal ranking signals, and
// "Relevance" already sorts by the blended recommended score.
export const ORDER_BY_VALUES = [
  "recommended_desc",
  "recent_asc",
  "recent_desc",
  "price_asc",
  "price_desc",
] as const;

export const BBOX_MAX_LNG_SPAN = 100;
export const BBOX_FALLBACK_HALF_SPAN = 30;

export function formatLocationWithFallback(location: SearchHookLocation): string {
  const formatted = formatLocation(location);
  if (formatted) return formatted;
  return location.context.country?.name ?? location.name ?? "";
}

export function formatLocationForZoom(
  location: SearchHookLocation,
  zoom: number,
): string {
  if (zoom < 5) {
    return location.context.country?.name ?? location.name ?? "";
  }

  if (zoom < 9) {
    if (location.context.region) {
      return location.context.region.name;
    }
    return location.context.country?.name ?? location.name ?? "";
  }

  return formatLocationWithFallback(location);
}

export function parseOrderByValue(value: OrderByValue) {
  const components = value.split("_");
  return {
    property: components[0] as ListingsOrderByProperty,
    direction: components[1] as ListingsOrderByDirection,
  };
}

/**
 * Geocoding APIs return bounding boxes that can be far too wide for the map
 * viewport. Two common cases:
 *
 * 1. Countries with far-flung territories (e.g. US bbox spans ~-179 to +179
 *    including Guam/American Samoa → ~360° span, map shows the whole world).
 * 2. Countries with distant outlying regions (e.g. US bbox spans -179 to -66
 *    including Alaska → 112° span, map zooms out excessively).
 *
 * When the span exceeds BBOX_MAX_LNG_SPAN, we build a constrained bbox
 * centered on the location's center coordinates (which geocoding APIs set to
 * the main/contiguous territory, not the geometric center of all territories).
 */
export function routeLocationPath(
  location: SearchHookLocation,
  countryCode?: string,
): string | null {
  const path = buildLocationUrlSegments(location, countryCode);
  return path ? `/businesses/${path}` : null;
}

/** A later businesses route should replace the place the search is showing. */
export function shouldAdoptRouteLocation(input: {
  pathname: string;
  previousPath: string | null;
  locationPath: string | null;
}): boolean {
  if (!input.locationPath) return false;
  if (input.previousPath === null) return false;
  if (input.pathname !== input.locationPath) return false;
  return input.previousPath !== input.pathname;
}

export function getSafeBounds(
  bbox: [number, number, number, number],
  center?: MapCoords,
): BoundingBox | null {
  const [swLng, swLat, neLng, neLat] = bbox;
  const lngSpan = neLng - swLng;

  if (lngSpan >= 0 && lngSpan <= BBOX_MAX_LNG_SPAN) {
    return { sw: { lat: swLat, lng: swLng }, ne: { lat: neLat, lng: neLng } };
  }

  if (!center) return null;

  return {
    sw: {
      lat: Math.max(center.lat - BBOX_FALLBACK_HALF_SPAN, -85),
      lng: center.lng - BBOX_FALLBACK_HALF_SPAN,
    },
    ne: {
      lat: Math.min(center.lat + BBOX_FALLBACK_HALF_SPAN, 85),
      lng: center.lng + BBOX_FALLBACK_HALF_SPAN,
    },
  };
}
